import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react'

import { type QueryKey, hashKey, useQueryClient } from '@tanstack/react-query'

import {
  type ChatRunSnapshot,
  type ChatStreamModel,
  type ChatStreamTopic,
  type HydratableChatSession,
  type SessionMetaSnapshot,
  createChatStreamModel,
} from '@/components/chat-v2/lib/chatStreamModel'

import type { ChatMessage, SubagentChatSnapshot } from '@/types/chat-v2'
import type { UUID } from '@/types/Id'

/**
 * Single agent-agnostic context for the socket-owned live-session model.
 * Adapters create the model per mount and provide it here; the socket and
 * runtime providers plus tool leaves read it directly — no per-agent context
 * threading.
 */
export const ChatStreamModelContext =
  React.createContext<ChatStreamModel | null>(null)

/**
 * Module-level fallback keeps hook ordering unconditional in surfaces without
 * a chat stream (search chat, isolated tests). It is never written to.
 */
const fallbackModel = createChatStreamModel()

export function useChatStreamModel(): ChatStreamModel {
  return useContext(ChatStreamModelContext) ?? fallbackModel
}

/** Subscribes to one model topic and reads a cached snapshot per change. */
export function useStreamTopic<T>(
  model: ChatStreamModel,
  topic: ChatStreamTopic,
  getSnapshot: () => T,
): T {
  const subscribe = useCallback(
    (onStoreChange: () => void) => model.subscribe(topic, onStoreChange),
    [model, topic],
  )
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function useChatRunSnapshot(model: ChatStreamModel): ChatRunSnapshot {
  const getSnapshot = useCallback(() => model.getRunSnapshot(), [model])
  return useStreamTopic(model, 'runState', getSnapshot)
}

export function useChatSessionMeta(
  model: ChatStreamModel,
): SessionMetaSnapshot {
  const getSnapshot = useCallback(() => model.getSessionMeta(), [model])
  return useStreamTopic(model, 'session', getSnapshot)
}

/**
 * Live thread arrays, guarded by session id so a model still holding the
 * previous session during a route transition never leaks across.
 *
 * `topic` picks the re-render cadence: `'thread:structure'` consumers ignore
 * content tokens (their message snapshots are refreshed on the next
 * structural render); `'thread:any'` consumers tick per token.
 */
export function useChatStreamThread(
  model: ChatStreamModel,
  sessionId: UUID,
  topic: 'thread:structure' | 'thread:any',
): {
  currentThread: ChatMessage[] | undefined
  allMessages: ChatMessage[] | undefined
} {
  const getCurrentThread = useCallback(
    () =>
      model.getSessionId() === sessionId
        ? // Widened at this boundary only: the model's cached arrays are
          // shared by identity for memoization, and the legacy pipeline is
          // mutable-typed but never mutates thread arrays (the same contract
          // react-query cache arrays always had). Copying here per render
          // would defeat identity-based caching.
          (model.getCurrentThread() as ChatMessage[])
        : undefined,
    [model, sessionId],
  )
  const getAllMessages = useCallback(
    () =>
      model.getSessionId() === sessionId
        ? (model.getAllMessages() as ChatMessage[])
        : undefined,
    [model, sessionId],
  )
  const currentThread = useStreamTopic(model, topic, getCurrentThread)
  const allMessages = useStreamTopic(model, topic, getAllMessages)
  return { currentThread, allMessages }
}

/**
 * Live text of one streaming part. Content tokens ping only this topic, so
 * the text leaf re-renders without any provider/repository work; the part in
 * the converted message carries the text as of the last structural event and
 * this overlay keeps the visible text current between them.
 */
export function useStreamingPartText(
  messageId: string | null | undefined,
  partIndex: number,
): string | undefined {
  const model = useChatStreamModel()
  const topic: ChatStreamTopic = `part:${messageId ?? ''}:${partIndex}`
  const getSnapshot = useCallback(
    () => (messageId ? model.getPartText(messageId, partIndex) : undefined),
    [model, messageId, partIndex],
  )
  return useStreamTopic(model, topic, getSnapshot)
}

const isHydratableSession = (value: unknown): value is HydratableChatSession =>
  typeof value === 'object' &&
  value !== null &&
  'id' in value &&
  'current_thread' in value &&
  'all_messages' in value

/**
 * Ingests server fetches of the session query into the model. Manual
 * `setQueryData` writes are the model's own boundary projections (run-boundary
 * write-backs, optimistic sends) and are skipped via react-query's `manual`
 * flag, so only real fetches — initial load, invalidation refetches — hydrate.
 */
export function useChatStreamModelHydration(
  model: ChatStreamModel,
  sessionQueryKey: QueryKey,
): void {
  const queryClient = useQueryClient()
  const queryHash = useMemo(() => hashKey(sessionQueryKey), [sessionQueryKey])

  useEffect(() => {
    // Catch up on a fetch that completed before this effect subscribed.
    const initial = queryClient.getQueryCache().get(queryHash)?.state.data
    if (isHydratableSession(initial) && model.getSessionId() !== initial.id) {
      model.hydrateSession(initial, 'fetch')
    }

    return queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryHash !== queryHash) return
      if (event.type !== 'updated' || event.action.type !== 'success') return
      if (event.action.manual) return
      const data = event.query.state.data
      if (isHydratableSession(data)) {
        model.hydrateSession(data, 'fetch')
      }
    })
  }, [model, queryClient, queryHash])
}
